import { API_CONFIG } from './apiConfig';
import { networkEventManager } from './networkEventManager';
import { AppUser, BackendSession } from '../types/auth';

// Helper function to process session data
const processSessionData = (sessionData: any): AppUser => ({
  username: sessionData.username,
  email: sessionData.email,
  backend: sessionData as BackendSession
});

// Auth API response types
export interface AuthPingResponse {
  success: boolean;
  status: number;
  error?: string;
}

export interface SessionResponse {
  success: boolean;
  user: AppUser | null;
  status: number;
  error?: string;
  pendingApproval?: boolean;
  /** Backend message when pending approval (e.g. user_not_approved) */
  message?: string;
  /** Backend suggestion when pending approval (e.g. details.suggestion) */
  suggestion?: string;
}

export interface UpdateSignatureResponse {
  success: boolean;
  signature?: string;
  error?: string;
}

/**
 * Centralized AuthService for all authentication-related API calls
 */
export class AuthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  /**
   * Perform auth ping to check session validity
   */
  public async ping(): Promise<AuthPingResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/ping/`, {
        method: 'GET',
        credentials: 'include',
      });

      const status = response.status;

      // Check for auth failures
      if (status === 401 || status === 403) {
        networkEventManager.emitAuthFailure(`Auth ping failed: ${status}`, { status });
        return {
          success: false,
          status,
          error: 'Authentication failed'
        };
      }

      // Check for server errors
      if (status >= 500) {
        networkEventManager.emitNetworkError(`Auth ping server error: ${status}`, { status });
        return {
          success: false,
          status,
          error: 'Server error'
        };
      }

      // Success cases (200, 204)
      if (status === 200 || status === 204) {
        return {
          success: true,
          status
        };
      }

      // Other status codes
      return {
        success: false,
        status,
        error: `Unexpected status: ${status}`
      };

    } catch (error) {
      networkEventManager.emitNetworkError('Auth ping network error', error);
      return {
        success: false,
        status: 0,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  /**
   * Get current session information
   * @param forceRefresh - If true, forces token refresh to get updated Cognito groups (used for approval check)
   */
  public async getSession(forceRefresh = false): Promise<SessionResponse> {
    try {
      const url = forceRefresh
        ? `${this.baseUrl}/auth/session/?force_refresh=true`
        : `${this.baseUrl}/auth/session/`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });

      const status = response.status;

      if (status === 204) {
        // Explicitly logged out (no cookie present)
        return {
          success: true,
          user: null,
          status
        };
      }

      if (response.ok) {
        const sessionData = await response.json();
        const user = processSessionData(sessionData);
        return {
          success: true,
          user,
          status
        };
      }

      // Handle pending approval (403 with specific error)
      if (status === 403) {
        try {
          const errorData = await response.json();
          if (errorData.error === 'user_not_approved') {
            return {
              success: false,
              user: null,
              status,
              error: 'pending_approval',
              pendingApproval: true,
              message: errorData.message,
              suggestion: errorData.details?.suggestion
            };
          }
        } catch {
          // If we can't parse the error, fall through to generic handling
        }
        networkEventManager.emitAuthFailure(`Session check failed: ${status}`, { status });
      } else if (status === 401) {
        networkEventManager.emitAuthFailure(`Session check failed: ${status}`, { status });
      } else if (status >= 500) {
        networkEventManager.emitNetworkError(`Session check server error: ${status}`, { status });
      }

      return {
        success: false,
        user: null,
        status,
        error: `Session check failed: ${status}`
      };

    } catch (error) {
      networkEventManager.emitNetworkError('Session check network error', error);
      return {
        success: false,
        user: null,
        status: 0,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  /**
   * Refresh session (alias for getSession for backward compatibility)
   */
  public async refreshSession(): Promise<SessionResponse> {
    return this.getSession();
  }

  /**
   * Check if user has valid session (for page refresh and app initialization)
   * Uses /api/v1/auth/ping/ endpoint which also triggers token refresh if needed
   *
   * Returns true if user is authenticated, false otherwise
   * On 401/403, returns false (not authenticated)
   * On network error, returns false (assume not authenticated)
   */
  public async checkSession(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/ping/`, {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Check if response includes authenticated field
        try {
          const data = await response.json();
          return data.authenticated === true;
        } catch {
          // If no JSON body, assume success means authenticated
          return true;
        }
      }

      // Auth failure - user not authenticated
      if (response.status === 401 || response.status === 403) {
        return false;
      }

      // Server error - assume not authenticated for safety
      if (response.status >= 500) {
        console.error('[Auth] checkSession server error:', response.status);
        return false;
      }

      // Other errors - assume not authenticated
      return false;

    } catch (error) {
      // Network error - assume not authenticated
      console.error('[Auth] checkSession network error:', error);
      return false;
    }
  }

  /**
   * Check if a response indicates auth failure
   */
  public isAuthFailure(response: Response | { status: number }): boolean {
    const status = response.status;
    return status === 401 || status === 403;
  }

  /**
   * Check if a response indicates server error
   */
  public isServerError(response: Response | { status: number }): boolean {
    const status = response.status;
    return status >= 500;
  }

  /**
   * Update user's signature
   */
  public async updateSignature(signature: string): Promise<UpdateSignatureResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/signature/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ signature }),
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          signature: data.signature
        };
      }

      // Handle error responses
      if (response.status === 401 || response.status === 403) {
        networkEventManager.emitAuthFailure(`Signature update failed: ${response.status}`, { status: response.status });
        return {
          success: false,
          error: 'Authentication required'
        };
      }

      return {
        success: false,
        error: `Failed to update signature: ${response.status}`
      };

    } catch (error) {
      networkEventManager.emitNetworkError('Signature update network error', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  public async changeTenant(tenantId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/tenants/${tenantId}/switch/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        return true;
      }

      if (response.status === 401 || response.status === 403) {
        networkEventManager.emitAuthFailure(`Change tenant failed: ${response.status}`, { status: response.status });
        return false;
      }

     throw new Error(`Change tenant failed: ${response.status}`);
    } catch (error) {
      networkEventManager.emitNetworkError('Change tenant network error', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const authService = new AuthService();
